import { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import Stack from "@mui/material/Stack";
import {
  ErrorMessage,
  ResponsiveDialog,
  DialogHeader,
} from "@/shared/components";

interface ConfirmDialogProps {
  open: boolean;
  onCancel: () => void;
  confirmText?: React.ReactNode;
  confirmTitle?: React.ReactNode;
  delay?: number;
  onConfirm: () => void;
  loading?: boolean;
  children?: React.ReactNode;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  error?: { message: string };
}

export function ConfirmDialog({
  open,
  onCancel,
  confirmText,
  confirmTitle,
  delay,
  onConfirm,
  loading,
  children,
  maxWidth = "xs",
  error,
}: ConfirmDialogProps) {
  const [time, setTime] = useState(delay || 0);

  const handleClose = () => {
    onCancel();
  };

  const onSubmit = (event: React.FormEvent) => {
    event.stopPropagation();
    event.preventDefault();
    onConfirm();
  };

  useEffect(() => {
    if (!open) return;
    if (!delay) return;

    const interval = setInterval(() => {
      setTime((prev) => prev - 1);
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [open, delay]);

  return (
    <ResponsiveDialog
      open={open}
      onClose={handleClose}
      maxWidth={maxWidth}
      fullWidth
      desktopHeight="auto"
    >
      <form onSubmit={onSubmit}>
        <DialogHeader
          title={confirmTitle as string}
          onClose={handleClose}
          loading={loading}
        />
        <DialogContent>
          <Stack spacing={2} mb={2}>
            {error && <ErrorMessage error={error} />}
            <DialogContentText>{confirmText}</DialogContentText>
            {children}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="inherit" variant="outlined">
            Cancel
          </Button>
          <Button
            disableElevation
            variant="contained"
            color="primary"
            type="submit"
            disabled={loading || (Boolean(delay) && time > 0)}
          >
            OK
            {time > 0 ? ` (${time})` : ""}
          </Button>
        </DialogActions>
      </form>
    </ResponsiveDialog>
  );
}
